import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Create a new notification
export const createNotification = async (notificationData) => {
  try {
    const notificationRef = await addDoc(collection(db, 'notifications'), {
      ...notificationData,
      createdAt: serverTimestamp(),
      status: 'active',
    });
    return notificationRef.id;
  } catch (error) {
    console.error('Error creating notification:', error);
    throw error;
  }
};

// Debug function to create test notifications
export const createTestNotifications = async () => {
  try {
    // Test notification for all users
    await createNotification({
      title: 'Test Notification for All Users',
      message: 'This is a test notification visible to all users.',
      priority: 'high',
      targetGroups: ['all'],
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days from now
      actionURL: null,
      imageURL: null
    });

    // Test notification for customers only
    await createNotification({
      title: 'Test Notification for Customers',
      message: 'This notification should only be visible to customers.',
      priority: 'medium',
      targetGroups: ['customers'],
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days from now
      actionURL: '/customer/profile',
      imageURL: null
    });

    // Test notification for partners only
    await createNotification({
      title: 'Test Notification for Partners',
      message: 'This notification should only be visible to partners.',
      priority: 'medium',
      targetGroups: ['partners'],
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)), // 7 days from now
      actionURL: '/partner/profile',
      imageURL: null
    });

    return 'Test notifications created successfully';
  } catch (error) {
    console.error('Error creating test notifications:', error);
    throw error;
  }
};

// Get all notifications
export const getAllNotifications = async () => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const q = query(notificationsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error getting notifications:', error);
    throw error;
  }
};

// Get active notifications
export const getActiveNotifications = async () => {
  try {
    const now = Timestamp.now();
    const notificationsRef = collection(db, 'notifications');
    const q = query(
      notificationsRef, 
      where('status', '==', 'active'),
      where('expiresAt', '>', now)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error getting active notifications:', error);
    throw error;
  }
};

// Get notifications for specific user type
export const getNotificationsForUserType = async (userType) => {
  try {
    const now = Timestamp.now();
    const notificationsRef = collection(db, 'notifications');
    
    // First get all active notifications that haven't expired
    const baseQuery = query(
      notificationsRef, 
      where('status', '==', 'active'),
      where('expiresAt', '>', now)
    );
    
    const querySnapshot = await getDocs(baseQuery);
    
    // Filter on client-side for matching the user type
    // This avoids Firestore compound query limitations
    return querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(notification => 
        notification.targetGroups &&
        (notification.targetGroups.includes('all') || notification.targetGroups.includes(userType))
      )
      .sort((a, b) => {
        // Sort by priority first (high, medium, low)
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        
        // Then sort by creation date (newest first)
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });
  } catch (error) {
    console.error(`Error getting notifications for ${userType}:`, error);
    throw error;
  }
};

// Get notification by ID
export const getNotificationById = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    const notificationSnap = await getDoc(notificationRef);
    
    if (notificationSnap.exists()) {
      return {
        id: notificationSnap.id,
        ...notificationSnap.data()
      };
    } else {
      throw new Error('Notification not found');
    }
  } catch (error) {
    console.error('Error getting notification:', error);
    throw error;
  }
};

// Update notification
export const updateNotification = async (notificationId, updates) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, updates);
    return true;
  } catch (error) {
    console.error('Error updating notification:', error);
    throw error;
  }
};

// Archive notification
export const archiveNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      status: 'archived'
    });
    return true;
  } catch (error) {
    console.error('Error archiving notification:', error);
    throw error;
  }
};

// Delete notification
export const deleteNotification = async (notificationId) => {
  try {
    const notificationRef = doc(db, 'notifications', notificationId);
    await deleteDoc(notificationRef);
    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

// Mark notification as read for a user
export const markNotificationAsRead = async (userId, notificationId) => {
  try {
    const userNotificationRef = doc(db, 'userNotifications', userId);
    const userNotificationSnap = await getDoc(userNotificationRef);
    
    if (userNotificationSnap.exists()) {
      // Update the existing document
      await updateDoc(userNotificationRef, {
        [`notifications.${notificationId}`]: {
          isRead: true,
          readAt: serverTimestamp()
        }
      });
    } else {
      // Create a new document
      await setDoc(userNotificationRef, {
        notifications: {
          [notificationId]: {
            isRead: true,
            readAt: serverTimestamp()
          }
        }
      });
    }
    return true;
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
};

// Get read status of notifications for a user
export const getUserNotificationStatus = async (userId) => {
  try {
    const userNotificationRef = doc(db, 'userNotifications', userId);
    const userNotificationSnap = await getDoc(userNotificationRef);
    
    if (userNotificationSnap.exists()) {
      return userNotificationSnap.data().notifications || {};
    } else {
      return {};
    }
  } catch (error) {
    console.error('Error getting user notification status:', error);
    throw error;
  }
}; 